import React from 'react';
import { useSelector } from 'react-redux';
import { Box, Button, CircularProgress, Dialog, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import InlineBanner from '../components/InlineBanner';
import useWaErrorText from './errorText';
import { WaRemovalChoice } from './waRemoval';

// SmartSend part B — "להסיר את הוואטסאפ" on a WHATSAPP-ONLY campaign (contract §6, v1.23c).
//
// 🔴 WHY THIS IS A SECOND DIALOG AND NOT A REWORDING OF THE FIRST.
// `picker.removeConfirm` says "המיפוי שהגדרת יימחק. המייל יישלח כרגיל." — which on a
// WhatsApp-only campaign is SWITCHING THE E-MAIL BACK ON IN SILENCE, the one thing the operator
// did not ask for. §6 already rules that "both channels off" is unreachable and that turning off
// the last one is cancelled; removing the WhatsApp here arrives at exactly that state through a
// different door, and it has no quiet answer. So there is a CHOICE, and it is the operator's:
//
//   א · switch to e-mail  → the leg goes, `SuppressEmail` with it, the mail goes to everyone
//   ב · delete everything → the leg, then the mapping (order mandatory — see `waRemoval.ts`)
//   ג · cancel
//
// 🔴 AND (א) IS NOT ALWAYS OFFERED. With no e-mail column in the source it would convert the
// campaign to a channel that REACHES NOBODY — which is precisely the case the whole feature was
// built for — so it is withheld, and `bodyNoEmail` says why rather than leaving a missing button
// to be guessed at.
//
// ⚠️ THE CONSEQUENCE LIVES INSIDE EACH LABEL, not in a paragraph above them (§7). One branch
// spends money and the other deletes work; an operator reads buttons.

interface Props {
    open: boolean;
    busy?: boolean;
    /**
     * 🔴 Whether "switch to e-mail" is a real option. Read from the SELECTED SOURCE's
     * `HasEmailIdentity`, never assumed — see the call sites.
     */
    canSwitchToEmail: boolean;
    /** The §4 `Message` of a failed LEG delete, or null. */
    error?: string | null;
    /**
     * 🔴 THE HALF-APPLIED STATE. The leg was deleted and the mapping delete then failed — the two
     * calls are not atomic (a known, accepted gap, §6). It gets its own flag rather than sharing
     * `error` because it is NOT the same situation: the WhatsApp IS gone, and the sentence that
     * belongs here is the e-mail side's own `manage.deleteFailed`, which describes exactly the
     * call that failed. Both action buttons are withdrawn while it is set — re-pressing either
     * would re-issue a delete that already succeeded, and §6 says not to invent a retry.
     */
    mappingDeleteFailed?: boolean;
    onChoose: (choice: WaRemovalChoice) => void;
    onCancel: () => void;
}

const WaRemoveWaOnlyDialog: React.FC<Props> = ({
    open, busy = false, canSwitchToEmail, error = null, mappingDeleteFailed = false,
    onChoose, onCancel,
}) => {
    const { t } = useTranslation();
    const { waErrorText } = useWaErrorText();
    const isRTL = useSelector((s: any) => s.core && s.core.isRTL);

    return (
        // `dir` is mandatory: MUI v4 Dialogs portal into document.body, outside App's <div dir>
        // (App.js:1018), and <html dir> is permanently "ltr" (App.js:727-730). Same treatment as
        // every other dialog in this feature.
        <Dialog open={open} onClose={busy ? undefined : onCancel} maxWidth="xs" fullWidth dir={isRTL ? 'rtl' : 'ltr'}>
            <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', borderBottom: '1px solid var(--pls-d-border, #e0e0e0)' }}>
                <Typography variant="h6">
                    {t('DataSources.send.whatsapp.mapping.removeWaOnly.title')}
                </Typography>
            </Box>
            <Box style={{ padding: 24 }}>
                {/* Written out in full, both arms. See the note at the top of this edit:
                    the i18n self-test's call-site scan cannot follow a composed key, and a key
                    it cannot follow is one nobody can grep from the string to the screen. */}
                <Typography variant="body2" color="textSecondary">
                    {canSwitchToEmail
                        ? t('DataSources.send.whatsapp.mapping.removeWaOnly.body')
                        : t('DataSources.send.whatsapp.mapping.removeWaOnly.bodyNoEmail')}
                </Typography>

                {/* `role="alert"`: the operator has already pressed a destructive button and is
                    looking at the buttons, not at the body — a refusal has to interrupt. */}
                {mappingDeleteFailed && (
                    <Box style={{ marginTop: 16 }}>
                        <InlineBanner
                            severity="error"
                            role="alert"
                            size="lg"
                            /* The e-mail side's OWN string for this exact call failing
                               (`SmartSendManageTab.tsx:128` uses it for the same thunk). It is
                               not a `send.whatsapp.*` key and does not belong in §7's group. */
                            title={t('DataSources.send.manage.deleteFailed')}
                        />
                    </Box>
                )}
                {!mappingDeleteFailed && error && (
                    <Box style={{ marginTop: 16 }}>
                        <InlineBanner severity="error" role="alert" size="lg" title={waErrorText(error)} />
                    </Box>
                )}
            </Box>
            <Box style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '16px 24px', borderTop: '1px solid var(--pls-d-border, #e0e0e0)' }}>
                {/* Stacked, not side by side: these are two full sentences, and a row would
                    truncate the very clause that carries the consequence. */}
                {!mappingDeleteFailed && canSwitchToEmail && (
                    <Button variant="contained" color="primary" disabled={busy}
                        onClick={() => onChoose('switchToEmail')}>
                        {busy
                            ? <CircularProgress size={18} color="inherit" />
                            : t('DataSources.send.whatsapp.mapping.removeWaOnly.switchToEmail')}
                    </Button>
                )}
                {!mappingDeleteFailed && (
                    <Button variant="outlined" disabled={busy} onClick={() => onChoose('deleteAll')}>
                        {busy
                            ? <CircularProgress size={18} />
                            : t('DataSources.send.whatsapp.mapping.removeWaOnly.deleteAll')}
                    </Button>
                )}
                {/* No autoFocus on anything destructive — the rule every confirm on this screen
                    follows. Cancel takes it, so an Enter key can never delete a mapping.
                    `picker.removeConfirm.cancel` is reused deliberately: §7 gives this dialog
                    five keys and no sixth for "ביטול", and minting one would fail the parity
                    test in the direction that catches a key in use and not on the list. */}
                <Button onClick={onCancel} disabled={busy} autoFocus>
                    {t('DataSources.send.whatsapp.picker.removeConfirm.cancel')}
                </Button>
            </Box>
        </Dialog>
    );
};

export default WaRemoveWaOnlyDialog;
